import * as React from 'react';
import { Clock, X } from 'lucide-react';
import { cn } from '@/lib/utils';

const p2 = (n) => String(n).padStart(2, '0');

// 'HH:MM:SS' oder 'HH:MM' → 'HH:MM', sonst ''
const fmt = (value) => {
  const s = String(value ?? '').trim();
  const m = s.match(/^(\d{1,2}):(\d{2})/);
  if (!m) return '';
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return '';
  return `${p2(h)}:${p2(min)}`;
};

// Akzeptiert: 16:30, 16.30, 1630, 930, 16, 9
// null = bewusst geleert, undefined = unbrauchbare Eingabe
export const parseTime = (text) => {
  const t = String(text ?? '').trim();
  if (!t) return null;
  let h;
  let min;
  let m = t.match(/^(\d{1,2})[:.](\d{1,2})$/);
  if (m) {
    h = Number(m[1]);
    min = Number(m[2]);
  } else if ((m = t.match(/^(\d{1,2})(\d{2})$/))) {
    h = Number(m[1]);
    min = Number(m[2]);
  } else if ((m = t.match(/^(\d{1,2})$/))) {
    h = Number(m[1]);
    min = 0;
  } else {
    return undefined;
  }
  if (h > 23 || min > 59) return undefined;
  return `${p2(h)}:${p2(min)}`;
};

// Eingaben, die ohne Blur übernommen werden. Safari gibt Buttons beim Klick keinen Fokus,
// sonst würde ein fertig getippter Wert beim direkten Klick auf Speichern verloren gehen.
const COMPLETE_TIME_RE = /^(?:\d{1,2}[:.]\d{2}|\d{3,4})$/;

// Drop-in-Ersatz für <input type="time">: onChange erhält { target: { value: 'HH:MM' } }.
// Anders als das native Feld zeigt es leer einen Platzhalter statt '--:--'.
const TimeInput = React.forwardRef(({ value, onChange, className, disabled, placeholder,
                                     clearable = true, onFocus, onEnterCommit, ...rest }, ref) => {
  const [text, setText] = React.useState(fmt(value));
  const emit = (v) => onChange && onChange({ target: { value: v } });

  React.useEffect(() => {
    setText(fmt(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const commitText = () => {
    const parsed = parseTime(text);
    if (parsed === null) { emit(''); setText(''); return; }
    if (parsed === undefined) { setText(fmt(value)); return; }
    emit(parsed);
    setText(parsed);
  };

  const step = (minutes) => {
    const base = parseTime(text) || fmt(value) || '00:00';
    const [h, min] = base.split(':').map(Number);
    let total = (h * 60 + min + minutes) % (24 * 60);
    if (total < 0) total += 24 * 60;
    const next = `${p2(Math.floor(total / 60))}:${p2(total % 60)}`;
    emit(next);
    setText(next);
  };

  return (
    <div
      title="Uhrzeit eintippen (z. B. 1630), ↑/↓ = 5 Minuten, Shift+↑/↓ = Stunde"
      className={cn(
        'flex h-10 w-full items-center gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2',
        disabled && 'cursor-not-allowed opacity-50',
        className
      )}
    >
      <Clock className="w-4 h-4 shrink-0 text-slate-400" />
      <input
        ref={ref}
        type="text"
        inputMode="numeric"
        value={text}
        disabled={disabled}
        placeholder={placeholder || 'HH:MM'}
        onChange={(e) => {
          const next = e.target.value;
          setText(next);
          if (COMPLETE_TIME_RE.test(next.trim())) {
            const parsed = parseTime(next);
            if (parsed) emit(parsed);
          }
        }}
        onFocus={(e) => {
          e.target.select();
          onFocus?.(e);
        }}
        onBlur={() => { commitText(); }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            commitText();
            e.target.blur();
            if (onEnterCommit) {
              queueMicrotask(() => onEnterCommit());
            }
            return;
          }
          if (e.key === 'Escape') { setText(fmt(value)); e.target.blur(); }
          if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            e.preventDefault();
            const delta = e.shiftKey ? 60 : 5;
            step(e.key === 'ArrowUp' ? delta : -delta);
          }
        }}
        className="flex-1 min-w-0 bg-transparent outline-none border-none p-0 text-inherit placeholder:text-muted-foreground disabled:cursor-not-allowed"
        {...rest}
      />
      {clearable && fmt(value) && !disabled && (
        <X
          className="w-3.5 h-3.5 shrink-0 cursor-pointer text-slate-400 hover:text-slate-700"
          onClick={(e) => { e.stopPropagation(); emit(''); setText(''); }}
        />
      )}
    </div>
  );
});
TimeInput.displayName = 'TimeInput';

export { TimeInput };
export default TimeInput;
